import { ROLE } from '@/constants';
import * as config from '@/shared/config/config';
import Vue from 'vue';
import PaymentService from '@/service/payment.service';
import { UserPlan } from '@/shared/model/account.model';
import { convertDateToCustomFormat } from '@/util/date-util';
import { checkIsGuestAccount } from '@/util/guest-util';
import { formatBytes } from '@/util/ds-util';

const store = config.initVueXStore(Vue);
const paymentService = new PaymentService();

export function isUltraUser(isOpenPopup = false) {
  if (checkIsGuestAccount(isOpenPopup)) return false;
  const localStorageUser = JSON.parse(localStorage.getItem('localStorageUser')) || null;
  if (localStorageUser?.authorities !== undefined && localStorageUser?.authorities?.length > 0) {
    const { authorities } = localStorageUser;
    const result = authorities.includes(ROLE.ROLE_PAID_ULTRA) && !authorities.includes(ROLE.ROLE_PAID_PRO);
    if (!result && isOpenPopup) {
      store.commit('appStore/setIsShowPopupRequiredPaidAccount', true);
      store.commit('appStore/setIsShowPopupRequiredOnlyUltra', true);
    }
    return !result;
  }
  return false;
}

export function isProUser(isOpenPopup = false) {
  if (checkIsGuestAccount(isOpenPopup)) return false;
  const localStorageUser = JSON.parse(localStorage.getItem('localStorageUser')) || null;
  if (localStorageUser?.authorities !== undefined && localStorageUser?.authorities?.length > 0) {
    const { authorities } = localStorageUser;
    return authorities.includes(ROLE.ROLE_PAID_PRO) && !authorities.includes(ROLE.ROLE_PAID_ULTRA);
  }
  return false;
}

export function checkLimitFeature(featureIdx: number, isOpenPopup = false) {
  const localStorageUser = JSON.parse(localStorage.getItem('localStorageUser')) || null;
  // check is paid first
  if (!isPaidUser(isOpenPopup)) {
    return false;
  }
  if (localStorageUser && localStorageUser.policyUsers) {
    const foundCount = localStorageUser.policyUsers?.find(item => item.featureIdx === featureIdx);
    if (foundCount && (foundCount.remain > 0 || foundCount.remain == -1)) {
      return true;
    }
    if (isOpenPopup) {
      store.commit('appStore/setIsShowFeatureLimit', true);
    }
    return false;
  }
}

export function isPaidUser(isOpenPopup = false) {
  // check is Guest first
  if (checkIsGuestAccount()) {
    store.commit('appStore/setIsShowPopupRequiredLogin', true);
    return false;
  }
  // check is paid user
  return checkIsPaidAccount(isOpenPopup);
}

export function getUserScrapAgentStorage(scrapAgentStorage) {
  if (!scrapAgentStorage) return;
  const { total, remain } = scrapAgentStorage;
  const usedStorage = total - remain;
  return {
    totalStorage: formatBytes(total),
    totalUsed: formatBytes(usedStorage),
    usagePercent: ((usedStorage / total) * 100).toFixed(2),
  };
}

export function checkIsPaidAccount(isOpenPopup?: boolean): boolean {
  let result = false;

  const localStorageUser = JSON.parse(localStorage.getItem('localStorageUser')) || null;

  if (localStorageUser !== undefined && localStorageUser !== null) {
    if (localStorageUser?.authorities !== undefined && localStorageUser?.authorities?.length > 0) {
      localStorageUser.authorities?.forEach(authority => {
        if (authority !== undefined && authority?.trim()?.length > 0 && authority.trim().includes('ROLE_PAID')) {
          result = true;
        }
      });
    } else {
      // todo
      // try to get information and check again
    }
  }
  if (!result && isOpenPopup !== undefined && isOpenPopup === true) {
    // store.commit('appStore/setIsShowPopupRequiredOnlyUltra', true);
    store.commit('appStore/setIsShowPopupRequiredPaidAccount', true);
  }

  return result;
}

export function getFreeTrialOrPaid() {
  const localStorageUser = JSON.parse(localStorage.getItem('localStorageUser')) || null;
  const userPlan = new UserPlan();
  if (localStorageUser) {
    const { startDateTrial, endDateTrial, startDatePaid, endDatePaid, authorities, stripeBalance } = localStorageUser;
    if (authorities && authorities.length > 0) {
      userPlan.stripeBalance = stripeBalance;
      if (authorities.includes(ROLE.ROLE_PAID_ULTRA)) {
        userPlan.planView = 'Ultra';
      } else if (authorities.includes(ROLE.ROLE_PAID_PRO)) {
        userPlan.planView = 'Pro';
      } else {
        userPlan.planView = 'Free';
      }
      if (userPlan.planView != 'Free' && endDateTrial && new Date(endDateTrial) > new Date()) {
        const startDate = convertDateToCustomFormat(startDateTrial, 'yyyy.MM.dd');
        const endDate = convertDateToCustomFormat(endDateTrial, 'yyyy.MM.dd');
        userPlan.period = startDate + ' ~ ' + endDate;
      } else if (endDatePaid && new Date(endDatePaid) > new Date()) {
        const startDate = convertDateToCustomFormat(startDatePaid, 'yyyy.MM.dd');
        const endDate = convertDateToCustomFormat(endDatePaid, 'yyyy.MM.dd');
        userPlan.period = startDate + ' ~ ' + endDate;
      }
    }
    return userPlan;
  }
}

export async function checkIsUsedFreeTrial() {
  return new Promise((resolve, reject) => {
    paymentService
      .getTrialingHistory()
      .then(res => {
        resolve(res.data);
      })
      .catch(error => {
        reject(error);
      });
  });
}

export async function addTrial() {
  return paymentService.addTrialForUser();
}

export function getSpecificRoleUser() {
  const localStorageUser = JSON.parse(localStorage.getItem('localStorageUser')) || null;
  if (localStorageUser !== undefined && localStorageUser !== null) {
    const { endDateTrial, authorities } = localStorageUser;
    let role = '';
    if (authorities !== undefined && authorities.length > 0) {
      authorities.forEach(authority => {
        if (authority.trim().includes('ROLE_PAID')) {
          if (authority.trim().includes('ROLE_PAID_ULTRA')) {
            role = 'Ultra';
          } else {
            if (endDateTrial) {
              role = 'Trial';
            } else {
              role = 'Pro';
            }
          }
        } else {
          role = 'Free';
        }
      });
    }
    return role;
  }
}

export function getRolePayloadForAPI(): string {
  const userRole = getSpecificRoleUser();
  switch (userRole.toLowerCase()) {
    case 'free':
      return 'ROLE_USER';
    case 'pro':
    case 'trial':
      return 'ROLE_PAID_PRO';
    case 'ultra':
      return 'ROLE_PAID_ULTRA';
    default:
      return 'ROLE_USER';
  }
}
